import { AuthService, CoreModule } from '@abp/ng.core';
import { registerLocale } from '@abp/ng.core/locale';
import { ThemeSharedModule } from '@abp/ng.theme.shared';
import { ValidationErrorModule } from '@abp/ng.theme.lepton-x';
import { LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ModalsComponent } from './modals/modals.component';
import { LoaderComponent } from './ui/loader/loader.component';
import { LogoutComponent } from './modals/logout/logout.component';
import { BtnComponent } from './ui/btn/btn.component';
import { HomeComponent } from './pages/customer/home/home.component';
import { HeaderComponent } from './ui/header/header.component';
import { CustomerPointsComponent } from './pages/common/customer-points/customer-points.component';
import { OfferComponent } from './pages/customer/home/offer/offer.component';
import { CreateCardComponent } from './pages/customer/create-card/create-card.component';
import { CreateCardTypeComponent } from './pages/common/create-card/card-type/card-type.component';
import { CreateCardCustomerTypeComponent } from './pages/common/create-card/customer-type/customer-type.component';
import {
  CreateCardCommunicationPreferencesComponent
} from './pages/common/create-card/communication-preferences/communication-preferences.component';
import {
  CreateCardPersonalInformationsComponent
} from './pages/common/create-card/personal-informations/personal-informations.component';
import { CreateCardProjectsComponent } from './pages/common/create-card/projects/projects.component';
import { CustomerInformationsComponent } from './pages/customer/customer-informations/customer-informations.component';
import { CreateCardSituationComponent } from './pages/common/create-card/situation/situation.component';
import { CustomerHistoryComponent } from './pages/customer/customer-history/customer-history.component';
import {
  CustomerPointsHistoryComponent
} from './pages/common/customer-history/customer-points-history/customer-points-history.component';
import {
  CustomerOrderHistoryComponent
} from './pages/common/customer-history/customer-order-history/customer-order-history.component';
import { CustomerLoginComponent } from './pages/customer/login/login.component';
import { OrderDetailComponent } from './modals/order-detail/order-detail.component';
import { httpInterceptorProviders } from './_helpers/http.interceptor';
import { AuthGuardService } from './_services/auth-guard.service';
import { CiamAbpAuthService } from './_services/ciam-abp-auth.service';
import { abpErrorPopupProvider } from './_services/abp-error-popup.handler';
import { GTM } from './_services/gtm.service';
import { SuccessComponent } from './modals/success/success.component';
import { PaymentMethodComponent } from './modals/payment-method/payment-method.component';
import { ResendPaymentLinkSmsComponent } from './modals/resend-payment-link-sms/resend-payment-link-sms.component';
import { CustomerVouchersComponent } from './pages/customer/customer-vouchers/customer-vouchers.component';
import { VouchersComponent } from './modals/vouchers/vouchers.component';
import { CustomerLoginFormComponent } from './pages/customer/login/login-form/login-form.component';
import {
  CustomerForgottenPasswordComponent
} from './pages/customer/login/forgotten-password/forgotten-password.component';
import { CustomerCreateCardInfoComponent } from './pages/customer/login/create-card-info/create-card-info.component';
import { CardInfosComponent } from './modals/card-infos/card-infos.component';
import {
  CustomerCardInformationComponent
} from './pages/common/customer-card-informations/customer-card-information.component';
import { CancelCardConfirmationComponent } from './modals/cancel-card-confirmation/cancel-card-confirmation.component';
import {
  PaymentContributionChoicesComponent
} from './pages/common/create-card/payment-contribution-choices/payment-contribution-choices.component';
import {
  FinalizationCreationComponent
} from './pages/common/create-card/finalization-creation/finalization-creation.component';
import { CardCreateComponent } from './pages/common/create-card/card-create/card-create.component';
import { SafePipeUrl } from './_helpers/safePipeUrl';
import { VoucherComponent } from './modals/vouchers/voucher/voucher.component';
import { EnvServiceProvider } from './env.service.provider';
import {
  ActivateCardConfirmationComponent
} from './modals/activate-card-confirmation/activate-card-confirmation.component';
import {
  CreateCardTermsConditionsComponent
} from './pages/common/create-card/terms-conditions/terms-conditions.component';
import { DisplayBarcodeComponent } from './modals/display-barcode/display-barcode.component';
import { Button } from 'primeng/button';
import { PaginatorModule } from 'primeng/paginator';
import { CustomerAuthCallbackComponent } from './pages/customer/auth-callback/auth-callback.component';
import { GenericPopupComponent } from './modals/generic-popup/generic-popup.component';
import { SafePipeHtml } from './_helpers/safePipeHtml';

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    BtnComponent,
    HeaderComponent,
    CustomerAuthCallbackComponent,
    CustomerLoginComponent,
    CustomerLoginFormComponent,
    CustomerForgottenPasswordComponent,
    CustomerCreateCardInfoComponent,
    OfferComponent,
    CreateCardComponent,
    CreateCardTypeComponent,
    CreateCardCustomerTypeComponent,
    CreateCardCommunicationPreferencesComponent,
    CreateCardTermsConditionsComponent,
    CreateCardPersonalInformationsComponent,
    CreateCardProjectsComponent,
    CustomerInformationsComponent,
    CreateCardSituationComponent,
    CustomerHistoryComponent,
    CustomerOrderHistoryComponent,
    CustomerPointsHistoryComponent,
    CustomerVouchersComponent,
    CustomerCardInformationComponent,
    PaymentContributionChoicesComponent,
    FinalizationCreationComponent,
    CardCreateComponent,

    /** Modals */
    ModalsComponent,
    LogoutComponent,
    OrderDetailComponent,
    SuccessComponent,
    PaymentMethodComponent,
    ResendPaymentLinkSmsComponent,
    VouchersComponent,
    VoucherComponent,
    CardInfosComponent,
    CancelCardConfirmationComponent,
    ActivateCardConfirmationComponent,
    DisplayBarcodeComponent,
    // Sert notamment à annoncer la perte de session CIAM depuis l'intercepteur HTTP.
    GenericPopupComponent,
    /** Fin modals */

    LoaderComponent,
    CustomerPointsComponent,
    SafePipeUrl,
    SafePipeHtml
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule.forRoot({
      environment,
      registerLocaleFn: registerLocale(),
    }),
    // `skipHandledErrorCodes` désarme le `RouterErrorHandlerService` d'ABP, qui affiche sa page
    // « [404] Ressource introuvable » sur *n'importe quelle* `NavigationError`. Le routeur de ce
    // site redirige `'**'` vers `/accueil` : une route réellement introuvable est impossible, donc
    // cette page ne s'affichait que pour masquer une erreur de garde. Liste vide et non absente :
    // seules les vraies navigations « Cannot match » restent traitées, et le filtrage des erreurs
    // HTTP est inchangé.
    ThemeSharedModule.forRoot({ httpErrorConfig: { skipHandledErrorCodes: [] } }),
    // Pas de thème LeptonX ici : le site client rend son propre en-tête et sa propre mise en page
    // (`AppComponent`), et aucun composant `lpx-*` n'est utilisé. Son initialiseur exigeait
    // `NAVIGATE_TO_MANAGE_PROFILE`, fourni par le seul `AbpOAuthModule` que le CIAM a retiré.
    //
    // En revanche LeptonX fournissait aussi `VALIDATION_ERROR_TEMPLATE`, que `ThemeSharedModule`
    // ne fournit pas : sans lui, les directives de `@ngx-validate/core` — actives d'office sur
    // tout `[formGroup]` / `[formControlName]` — échouent à l'injection et font tomber la page
    // entière (création de carte, informations client…). Ce sous-module n'embarque que le
    // composant d'affichage des erreurs, pas la mise en page ni l'initialiseur du thème.
    ValidationErrorModule.forRoot(),
    Button,
    PaginatorModule
  ],
  providers: [
    httpInterceptorProviders,
    AuthGuardService,
    // Les écrans d'erreur d'ABP sont stylés par LeptonX, que ce site ne charge plus : on rend ses
    // erreurs HTTP dans la popup du site.
    abpErrorPopupProvider,
    // Remplace le repli d'ABP, dont chaque méthode journalise une erreur faute de paquet d'auth.
    { provide: AuthService, useClass: CiamAbpAuthService },
    EnvServiceProvider,
    GTM,
    { provide: LOCALE_ID, useValue: 'fr' },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
